import { TTextLocalized } from '@northladder/utilities';

/**
 * Enum type for the auto assessments filter name.
 */
export enum IAssessmentsFilterName {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  IMG_RE_UPLOAD = 'IMG_RE_UPLOAD',
  REJECTED = 'REJECTED',
  EXPIRED = 'EXPIRED',
}

/**
 * Interface for the auto assessments filter list.
 */
export interface IAutoAssessmentFilterOptions {
  count: number;
  key: IAssessmentsFilterName;
  label: string;
  label_ar: string;
}

export interface IAutoAssessmentFilterFormattedResponse {
  count: number;
  key: IAssessmentsFilterName;
  label: TTextLocalized;
}

/**
 * Interface for the auto assessments filter data.
 */
export interface IAutoAssessmentsFilterData {
  filterOptions: IAutoAssessmentFilterFormattedResponse[];
}

/**
 * Response type of the auto assessments filter API.
 */
export interface IAutoAssessmentsFilterResponse {
  data: IAutoAssessmentFilterOptions[];
  message: string;
  status: boolean;
}

/**
 * Interface for the request lists api payload
 */
export interface IAutoAssessmentsRequestsListPayload {
  page: number;
  limit: number;
  search?: string;
  status?: IAssessmentsFilterName[];
  sort?: {
    field?: string;
    order?: 'asc' | 'desc';
  };
}

/**
 * interface for assessment filters payload
 */
export interface IAutoAssessmentsFiltersPayload {
  search?: string;
}

export interface ICosmeticPayloadOptions {
  component: string;
  condition: string;
}

export interface IReuploadImagesOptions {
  position: string;
  retakeReasonIds: string[];
  comment?: string;
}

/**
 * interface for retaking/submitting the assessment request payload
 */
export interface IUpdateAssessmentRequestPayload {
  cosmetic?: ICosmeticPayloadOptions[];
  identifier?: string;
  reuploadImages: IReuploadImagesOptions[] | [];
  sessionId: string;
}

/**
 * Interface for the asset detail raw data.
 */
export interface IAssetDetailRaw {
  _id: string;
  label: string;
  label_ar: string;
}

/**
 * interface for each individual status raw
 */
export interface IAssessmentStatusRaw {
  key: IAssessmentsFilterName;
  label: string;
  label_ar: string;
}

/**
 * interface for requests lists raw data
 */
export interface IAutoAssessmentsRequestsListRaw {
  _id: string;
  assetDetail: IAssetDetailRaw;
  sessionId: string;
  status: IAssessmentStatusRaw;
  thumbnail: string;
  imageCount: number;
  updatedDate: number;
  user?: {
    name: string;
  };
}

/**
 * interface for formatted Asset detail
 */
export interface IFormattedAssetDetail {
  assetId?: string;
  label?: TTextLocalized;
}

/**
 * interface for formatted status for a request
 */
export interface IFormattedStatus {
  key: IAssessmentsFilterName;
  label: TTextLocalized;
  labelStr: string;
}

export interface IAssetDetail {
  assetId: string;
  label: TTextLocalized;
  labelStr: string;
}

/**
 * interface for formatted requests list.
 */
export interface IFormattedAutoAssessmentsRequestsList {
  requestId: string;
  assetDetail: IAssetDetail;
  sessionId: string;
  imageCount: number;
  status: IFormattedStatus;
  thumbnail: string;
  updatedDate: number;
  user?: {
    name: string;
  };
}

export interface IFormattedAutoAssessmentsRequest {
  title: string;
  data: IFormattedAutoAssessmentsRequestsList[];
}

/**
 * interface for formatted auto assessments requests list data
 */
export interface IFormattedAutoAssessmentsRequestsListData {
  requests: IFormattedAutoAssessmentsRequest[];
  total: number;
}

/**
 * interface for raw requests response
 */
export interface IAutoAssessmentsRequestsListItem {
  [key: string]: IAutoAssessmentsRequestsListRaw[];
}

/**
 * Interface for the auto assessments request list data.
 */
export interface IFetchAutoAssessmentsRequestsListData {
  imageBaseURL: string;
  requests: IAutoAssessmentsRequestsListItem;
  total: number;
}

/**
 * Interface for the auto assessments request list response.
 */
export interface IAutoAssessmentsRequestsListRawResponse {
  data: IFetchAutoAssessmentsRequestsListData;
  message: string;
  status: boolean;
}

/**
 * interface for the assessment detail conditions raw data.
 */
export interface IAssessmentDetailConditionsRaw {
  label: string;
  label_ar?: string;
  value: string;
}

/**
 * interface for the assessment detail cosmetic raw data.
 */
export interface IAssessmentDetailCosmeticRaw {
  checkName: string;
  conditions: IAssessmentDetailConditionsRaw[];
}

/**
 * enum type for the assessment detail image position.
 */
export enum IAssessmentDetailImagePosition {
  FRONT_WHITE = 'FRONT_WHITE',
  FRONT_BLACK = 'FRONT_BLACK',
  BACK = 'BACK',
  LEFT = 'LEFT',
  RIGHT = 'RIGHT',
  TOP = 'TOP',
  BOTTOM = 'BOTTOM',
}

/**
 * enum type for assessment detail image id.
 */
export enum IAssessmentDetailImageId {
  GRADE_PRO_FRONT_WHITE_IMG = 'GRADE_PRO_FRONT_WHITE_IMG',
  GRADE_PRO_FRONT_BLACK_IMG = 'GRADE_PRO_FRONT_BLACK_IMG',
  GRADE_PRO_BACK_IMG = 'GRADE_PRO_BACK_IMG',
  GRADE_PRO_LEFT_IMG = 'GRADE_PRO_LEFT_IMG',
  GRADE_PRO_RIGHT_IMG = 'GRADE_PRO_RIGHT_IMG',
  GRADE_PRO_TOP_IMG = 'GRADE_PRO_TOP_IMG',
  GRADE_PRO_BOTTOM_IMG = 'GRADE_PRO_BOTTOM_IMG',
}

/**
 * interface for the assessment detail image raw data.
 */
export interface IAssessmentDetailImagesRaw {
  position: IAssessmentDetailImagePosition;
  imageId: IAssessmentDetailImageId;
  fileUploadedPath: string;
  uploadedOn: number;
}

export interface IAssessmentDetailStoreInfoRaw {
  _id: string;
  name: string;
  headOfficeId: string;
  headOfficeName: string;
  currency: string;
}

export interface IAssessmentDetailReviewedByInfoRaw {
  _id: string;
  email: string;
  name: string;
}

export interface IAssessmentDetailConditionInfoRaw {
  conditionId: string;
  condition: string;
  percentage: number;
}

export interface IExistingDealRaw {
  condition: {
    conditionId: string;
    condition: string;
    percentage: number;
  };
  createdDate: number;
  reviewedByInfo: {
    name: string;
  };
  storeInfo: {
    name: string;
  };
}

/**
 * interface for the assessment requests raw data.
 */
export interface IAssessmentRequestsRawData {
  assetDetail: IAssetDetailRaw;
  assessmentStatus: IAssessmentStatusRaw[];
  /**
   * @deprecated not being used at the moment not used in UI instead using the
   * assessmentStatus[0]
   */
  autoAssessmentType: string;
  conditionInfo?: IAssessmentDetailConditionInfoRaw;
  cosmetic: IAssessmentDetailCosmeticRaw[];
  createdDate: number;
  existingDeal?: IExistingDealRaw;
  imageBaseURL: string;
  images: IAssessmentDetailImagesRaw[];
  reviewedByInfo?: IAssessmentDetailReviewedByInfoRaw;
  sessionId: string;
  storeInfo?: IAssessmentDetailStoreInfoRaw;
}

/**
 * interface for the assessment request details raw response.
 */
export interface IAssessmentRequestsDetailsPendingResponse {
  data: IAssessmentRequestsRawData;
  message: string;
  status: boolean;
  isRequestBlocked?: never;
}

export interface IAssessmentRequestDetailsBlockedResponse {
  data?: IAssessmentRequestsRawData;
  message: string;
  status: boolean;
  isRequestBlocked: boolean;
}

export type TAssessmentRequestDetailsRawResponse =
  | IAssessmentRequestsDetailsPendingResponse
  | IAssessmentRequestDetailsBlockedResponse;

/**
 * interface for the assessment details conditions formatted data.
 */
export interface IAssessmentDetailsConditionsFormatted {
  label: TTextLocalized;
  value: string;
}

/**
 * interface for the assessment details conditions formatted data.
 */
export interface IAssessmentCosmeticDetails {
  checkName: string;
  conditions: IAssessmentDetailsConditionsFormatted[];
}

export type IAssessmentDetailStoreInfo = Pick<
  IAssessmentDetailStoreInfoRaw,
  'headOfficeName' | 'name'
>;

export type IAssessmentDetailReviewedByInfo = Pick<
  IAssessmentDetailReviewedByInfoRaw,
  'name'
>;

export interface IExistingDeal {
  condition: string;
  createdDate: number;
  reviewedBy: string;
  storeInfo: string;
}

/**
 * interface for the assessment request details formatted data.
 */
export interface IAssessmentRequestsDetailsFormattedData {
  assetDetail: IFormattedAssetDetail;
  autoAssessmentStatuses: IFormattedStatus[];
  /**
   * @deprecated not being used at the moment not used in UI instead using the
   * assessmentStatuses[0]
   */
  autoAssessmentType: string | null;
  condition: string | null;
  cosmetics: IAssessmentCosmeticDetails[];
  createdDate: number;
  existingDeal: IExistingDeal | null;
  imageBaseURL: string | null;
  images: IAssessmentDetailImagesRaw[];
  reviewedByInfo: IAssessmentDetailReviewedByInfo | null;
  sessionId: string;
  storeInfo: IAssessmentDetailStoreInfo | null;
  isPendingRequest: boolean;
}

export interface IAssessmentRequestsDetailsBlocked {
  type: 'BLOCKED';
  message: string;
  isRequestBlocked: boolean;
}

export interface IAssessmentRequestsDetailsPending {
  type: 'PENDING';
  content: IAssessmentRequestsDetailsFormattedData;
}

export type TAssessmentRequestsDetailsData =
  | IAssessmentRequestsDetailsPending
  | IAssessmentRequestsDetailsBlocked;

export interface IAssessmentDetailsResponse {
  message: string;
  status: boolean;
}

/**
 * interface for retake reasons formatted response data
 */
export interface IAutoAssessmentsRetakeReasonsFormattedData {
  description: TTextLocalized;
  reasonId: string;
  reason: TTextLocalized;
}

/**
 * interface for retake reasons raw data
 */
export interface IAssessmentsRetakeReasonsRawData {
  _id: string;
  description: string;
  description_ar?: string;
  reason: string;
  reason_ar?: string;
}

/**
 * interface for retake reasons raw response
 */
export interface IAssessmentsRetakeReasonsRawResponse {
  status: boolean;
  message: string;
  data: IAssessmentsRetakeReasonsRawData[];
}

export interface IAssessmentRetakeSubmissionRawData {
  _id: string;
  event: string;
  autoAssessmentType: string;
  log: {
    nlAssetId: string;
    nlAssetName: string;
    referenceId: string;
    images: IAssessmentDetailImagesRaw[];
  };
  storeId: string;
  imageUploadStatus: string;
  createdDate: number;
  updatedDate: number;
  status: boolean;
  assessmentStatus: string;
  isInReview: boolean;
  reviewedBy: string;
  updatedBy: string;
  reUploadReqCount: number;
}

/**
 * interface for assessment submission raw response
 */
export interface IAssessmentSubmissionRawResponse {
  data?: IAssessmentRetakeSubmissionRawData;
  status: boolean;
  message: string;
}

/**
 * interface for unlocking the request raw response
 */
export interface IAssessmentUnlockRequestRawResponse {
  status: boolean;
  message: string;
}

export interface IAssessmentsOnSuccessParam {
  message: string;
  status: boolean;
}
